import type { TFunction } from 'i18next'
import { getMenuTitleFromPath } from '@/utils/platformMenu'

/** Map URL path segments to i18n keys (menu.* or routes.*). */
const ROUTE_SEGMENT_KEYS: Record<string, string> = {
  dashboard: 'menu.dashboard',
  analytics: 'menu.analytics',
  sale: 'routes.sale',
  wholesale: 'routes.wholesale',
  inventory: 'menu.inventory',
  expenses: 'menu.expenses',
  activitylogs: 'menu.activitylogs',
  notifications: 'menu.notifications',
  telegram: 'menu.telegram',
  config: 'menu.config',
  users: 'menu.users',
  categories: 'menu.categories',
  settings: 'menu.settings',
  reports: 'menu.reports',
  outofstock: 'menu.outofstock',
  lowinventory: 'menu.lowinventory',
  profile: 'menu.profile',
  sampleviewing: 'menu.sampleviewing',
  samplesholdings: 'menu.sampleholdings',
  sampleviewingworker: 'menu.sampleviewingmanagement',
  platform: 'menu.sections.platform',
  tenants: 'menu.platform-tenants',
  plans: 'menu.platform-plans',
  subscriptions: 'menu.platform-subscriptions',
  integrations: 'menu.platform-integrations',
  sales: 'menu.dashboard',
  add: 'common.add',
}

/** URL tail segments that are actions/IDs, not page names. */
const GENERIC_ROUTE_SEGMENTS = new Set(['add', 'edit', 'new', 'create'])

function getTitleSegmentFromPath(paths: string[]): string {
  for (let i = paths.length - 1; i >= 0; i--) {
    const segment = paths[i]
    if (GENERIC_ROUTE_SEGMENTS.has(segment)) continue
    if (/^[a-f0-9]{24}$/i.test(segment)) continue
    return segment
  }
  return paths[paths.length - 1] ?? 'dashboard'
}

const ROUTE_DEFAULTS: Record<string, string> = {
  'routes.sale': 'New Order',
  'routes.wholesale': 'Accounts',
  'routes.details': 'Details',
  'menu.dashboard': 'Dashboard',
  'menu.analytics': 'Analytics',
}

export const ROUTE_SEGMENT_LABELS: Record<string, string> = {
  sale: 'routes.sale',
  wholesale: 'routes.wholesale',
}

export function formatRouteSegment(segment: string, t?: TFunction): string {
  const labelKey = ROUTE_SEGMENT_KEYS[segment] ?? ROUTE_SEGMENT_LABELS[segment]
  if (labelKey && t) {
    return t(labelKey, { defaultValue: ROUTE_DEFAULTS[labelKey] ?? segment })
  }
  if (labelKey) {
    return ROUTE_DEFAULTS[labelKey] ?? segment
  }
  if (/^[a-f0-9]{24}$/i.test(segment)) {
    return t ? t('routes.details', { defaultValue: 'Details' }) : 'Details'
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
}

export function getPageTitleFromPath(pathname: string, t?: TFunction): string {
  const menuTitle = getMenuTitleFromPath(pathname, t)
  if (menuTitle) return menuTitle

  const paths = pathname.split('/').filter(Boolean)
  if (paths.length === 0) {
    return t ? t('menu.dashboard', { defaultValue: 'Dashboard' }) : 'Dashboard'
  }

  const titleSegment = getTitleSegmentFromPath(paths)
  return formatRouteSegment(titleSegment, t)
}
